import { useState } from 'react';
import { Link } from '@backstage/core-components';
import { useRouteRef } from '@backstage/core-plugin-api';
import { entityRouteRef } from '@backstage/plugin-catalog-react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  makeStyles,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { formatDuration, formatStarted } from '../catalog/pipeline/format';
import { outcomeOf } from '../catalog/pipeline/outcome';
import { OutcomePill } from '../catalog/pipeline/OutcomePill';
import type { DeploymentRecord } from './snapshot';
import type { ServiceName } from './useServiceNames';
import { visuallyHidden } from '../visuallyHidden';
import { CustomerProjectChip } from '../CustomerProjectChip';

const PAGE_SIZE = 10;

const useStyles = makeStyles(theme => ({
  // Fixed layout: the columns share the page's width instead of the longest
  // workflow name setting it, so the table never runs past the page's edge.
  table: { tableLayout: 'fixed', width: '100%' },
  cell: {
    padding: theme.spacing(1, 1.25),
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  mono: { fontFamily: 'var(--font-mono)', fontSize: '0.8rem' },
  repo: {
    display: 'block',
    fontFamily: 'var(--font-mono)',
    fontSize: '0.75rem',
    color: 'var(--ui-text-muted)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  environment: {
    display: 'inline-block',
    padding: '0 8px',
    borderRadius: 10,
    border: '1px solid transparent',
    fontSize: '0.75rem',
    fontWeight: 600,
    lineHeight: 1.6,
    whiteSpace: 'nowrap',
    background: 'rgba(110, 118, 129, 0.15)',
    color: 'var(--ui-text-muted)',
  },
  production: { background: 'rgba(46, 160, 67, 0.15)', color: '#2EA043' },
  // Derived rather than read: the same colour, a dashed edge, and the word in
  // the accessible text — never the colour alone.
  derived: { borderStyle: 'dashed', borderColor: 'currentColor' },
  visuallyHidden,
  compactList: { listStyle: 'none', margin: 0, padding: 0 },
  compactRow: {
    padding: theme.spacing(1.25, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    minWidth: 0,
  },
  compactMeta: {
    fontSize: '0.8rem',
    color: 'var(--ui-text-muted)',
    overflowWrap: 'anywhere',
  },
  footer: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    fontSize: '0.8rem',
    color: 'var(--ui-text-muted)',
  },
}));

const DERIVED_TITLE =
  'Derived: the workflow names no environment, and every workflow that names none deploys to production';

const EnvironmentPill = ({ deploy }: { deploy: DeploymentRecord }) => {
  const classes = useStyles();
  if (!deploy.environment) return <span>Unknown</span>;
  const tone = deploy.environment === 'production' ? classes.production : '';
  const derived = deploy.environmentDerived ? classes.derived : '';
  return (
    <span
      className={`${classes.environment} ${tone} ${derived}`}
      title={deploy.environmentDerived ? DERIVED_TITLE : undefined}
    >
      {deploy.environment}
      {deploy.environmentDerived && (
        <span className={classes.visuallyHidden}> (derived)</span>
      )}
    </span>
  );
};

function tookOf(deploy: DeploymentRecord): string {
  if (deploy.durationSec !== null) return formatDuration(deploy.durationSec);
  return outcomeOf(deploy).kind === 'running' ? 'In progress' : 'Unknown';
}

const Started = ({ iso }: { iso: string }) => {
  const started = formatStarted(iso, Date.now());
  if (!started) return <>Unknown</>;
  return (
    <time dateTime={iso} title={started.exact}>
      {started.label}
    </time>
  );
};

/**
 * The service as the catalog names it, the repository beneath (AC-2), and a
 * customer project marked as one (specs/customer-projects.md AC-1).
 */
const Service = ({
  deploy,
  services,
}: {
  deploy: DeploymentRecord;
  services: Map<string, ServiceName>;
}) => {
  const classes = useStyles();
  const entityRoute = useRouteRef(entityRouteRef);
  const { slug } = deploy;
  const service = services.get(slug.toLowerCase());
  const mark = deploy.customerProject && <CustomerProjectChip />;
  if (!service) {
    return (
      <>
        <span className={classes.mono}>{slug}</span>
        {mark}
      </>
    );
  }
  return (
    <>
      <Link
        to={entityRoute({
          kind: service.kind.toLowerCase(),
          namespace: service.namespace.toLowerCase(),
          name: service.name,
        })}
      >
        {service.title}
      </Link>
      <span className={classes.repo}>{slug}</span>
      {mark}
    </>
  );
};

/**
 * The deploys the filters leave, a page at a time (specs/deployments.md AC-1,
 * AC-2, AC-10).
 *
 * Each names its service as the catalog does and leads to the run that
 * performed it. Below the medium breakpoint seven columns do not fit beside the
 * navigation, so each deploy becomes a two-line entry — service and outcome
 * above, the rest beneath — and nothing scrolls sideways.
 *
 * Mounted afresh whenever the filters change (the page keys it on them), so a
 * narrowed list always opens on its first page.
 */
export const DeploysList = ({
  deploys,
  services,
  source,
}: {
  deploys: DeploymentRecord[];
  services: Map<string, ServiceName>;
  /** When the snapshot was collected, for the source line. */
  source: string | null;
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const narrow = useMediaQuery(theme.breakpoints.down('sm'));
  const [page, setPage] = useState(0);
  const pageDeploys = deploys.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const collected = source ? formatStarted(source, Date.now()) : undefined;

  return (
    <>
      {narrow ? (
        <ul className={classes.compactList} aria-label="Deploys">
          {pageDeploys.map(deploy => (
            <li key={deploy.htmlUrl} className={classes.compactRow}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
                gridGap={8}
                minWidth={0}
              >
                <Box minWidth={0}>
                  <Service deploy={deploy} services={services} />
                </Box>
                <OutcomePill outcome={outcomeOf(deploy)} />
              </Box>
              <div className={classes.compactMeta}>
                <Link to={deploy.htmlUrl}>{deploy.workflow}</Link>
                {' · '}
                <EnvironmentPill deploy={deploy} />
                {' · '}
                {[deploy.branch ?? 'Unknown', tookOf(deploy)].join(' · ')}
                {' · '}
                <Started iso={deploy.createdAt} />
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <Table size="small" aria-label="Deploys" className={classes.table}>
          <colgroup>
            <col style={{ width: '11%' }} />
            <col style={{ width: '22%' }} />
            <col style={{ width: '22%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '11%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '12%' }} />
          </colgroup>
          <TableHead>
            <TableRow>
              {[
                'Status',
                'Service',
                'Workflow',
                'Environment',
                'Branch',
                'Duration',
              ].map(title => (
                <TableCell key={title} className={classes.cell}>
                  {title}
                </TableCell>
              ))}
              <TableCell align="right" className={classes.cell}>
                When
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pageDeploys.map(deploy => (
              <TableRow key={deploy.htmlUrl}>
                <TableCell className={classes.cell}>
                  <OutcomePill outcome={outcomeOf(deploy)} />
                </TableCell>
                <TableCell className={classes.cell}>
                  <Service deploy={deploy} services={services} />
                </TableCell>
                <TableCell className={classes.cell} title={deploy.workflow}>
                  <Link to={deploy.htmlUrl}>{deploy.workflow}</Link>
                </TableCell>
                <TableCell className={classes.cell}>
                  <EnvironmentPill deploy={deploy} />
                </TableCell>
                <TableCell
                  className={`${classes.cell} ${classes.mono}`}
                  title={deploy.branch ?? undefined}
                >
                  {deploy.branch ?? 'Unknown'}
                </TableCell>
                <TableCell className={classes.cell}>{tookOf(deploy)}</TableCell>
                <TableCell align="right" className={classes.cell}>
                  <Started iso={deploy.createdAt} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <div className={classes.footer}>
        <span>
          Source: github-insights
          {collected && (
            <>
              {' · collected '}
              <time dateTime={source ?? undefined} title={collected.exact}>
                {collected.label}
              </time>
            </>
          )}
        </span>
        <TablePagination
          component="div"
          count={deploys.length}
          page={page}
          rowsPerPage={PAGE_SIZE}
          rowsPerPageOptions={[]}
          onPageChange={(_e, next) => setPage(next)}
        />
      </div>
    </>
  );
};
